import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl, defineMessages} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    useCustomer,
    useCustomerId,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {} from '@salesforce/commerce-sdk-react'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

import {
    Box,
    Heading,
    Text,
    Stack,
    Flex,
    Button,
    useDisclosure,
    Alert,
    AlertIcon,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Divider} from '@chakra-ui/react'
import Field from '../../components/field'
import Link from '@salesforce/retail-react-app/app/components/link'
import {LeftArrow, InfoCircleBlack, CheckCircleIcon} from '../../components/icons'
import useCommunicationsFields from '../../components/forms/useCommunicationsFields'
import SMSOptInModal from './sms-optin-modal'
import {useForm} from 'react-hook-form'
import {sendViewErrorEvent} from '../../utils/gtm-utils'
import usedDeviceDetection from '../../hooks/use-device-detection'

const CommunicationPreferences = ({prefix = ''}) => {
    const {formatMessage} = useIntl()
    const {isOpen, onOpen, onClose} = useDisclosure()
    // UI-only mock state (not backend integrated yet, will be replaced once backend decision is confirmed)
    const [isSmsSubscribed, setIsSmsSubscribed] = useState(false)
    const [tempPhone, setTempPhone] = useState('')

    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const SMS_OPTIN_LOYALTY_ENABLED = getSiteEnvVarValue(config.app, 'SMS_OPTIN_LOYALTY_ENABLED', siteId)
    const SFSC_EMAIL_SUBSCRIPTION_ENABLED = getSiteEnvVarValue(config.app, 'SFSC_EMAIL_SUBSCRIPTION_ENABLED', siteId)
    const isSMSOptInEnabled = SMS_OPTIN_LOYALTY_ENABLED === 'true'
    const isSfscEmailSubscriptionEnabled = SFSC_EMAIL_SUBSCRIPTION_ENABLED == 'true'
    const customerId = useCustomerId()
    const {data: customer} = useCustomer({parameters: {customerId}}, {enabled: !!customerId})
    const {mutate: updateEmailPreferences, isLoading} =
        useShopperCustomersMutation('updateCustomer')

    const maskPhone = (phone = '') => {
        const clean = phone.replace(/\D/g, '')

        if (clean.length <= 4) return clean
        const last4 = clean.slice(-4)
        return `(···) ··· - ${last4}`
    }

    const form = useForm({
        defaultValues: {
            [`${prefix}emailGarageOptIn`]: false,
            [`${prefix}emailGaragePersonalizedOptIn`]: false,
            [`${prefix}languagePreference`]: 'fr',
            [`${prefix}smsPhoneNumber`]: ''
        }
    })

    const fields = useCommunicationsFields({form, prefix})

    const headingRef = useRef()

    const device = usedDeviceDetection()

    useEffect(() => {
        headingRef?.current?.focus()
    }, [])

    useEffect(() => {
        if (customer) {
            form.reset({
                [`${prefix}emailGarageOptIn`]: customer.c_email_garage_optedIn ?? false,
                [`${prefix}emailGaragePersonalizedOptIn`]:
                    customer.c_email_garage_personalized_optedIn ?? false,
                [`${prefix}languagePreference`]: 'fr'
            })
        }
    }, [customer, prefix, form])

    const onSubmit = ({emailGarageOptIn, emailGaragePersonalizedOptIn}) => {
        updateEmailPreferences(
            {
                parameters: {
                    customerId
                },
                body: {
                    c_email_garage_optedIn: emailGarageOptIn,
                    c_email_garage_personalized_optedIn: emailGaragePersonalizedOptIn,
                    c_device: device
                }
            },
            {
                onError() {
                    // GTM Modification
                    sendViewErrorEvent('Failed to update email preferences. Please try again.')
                    form.setError('global', {
                        type: 'manual',
                        message: formatMessage({
                            id: 'communication_preferences.email.error_message',
                            defaultMessage: 'Failed to update email preferences. Please try again.'
                        })
                    })
                }
            }
        )
    }

    return (
        <Box mx="auto">
            <Stack spacing={6} width={{base: 'full', lg: '458px'}}>
                <Heading
                    as="h1"
                    fontSize={{base: '20px', lg: '24px'}}
                    fontWeight="bold"
                    textTransform="uppercase"
                    tabIndex="0"
                    ref={headingRef}
                    letterSpacing="wide"
                >
                    <Flex alignItems="center" gap="25px">
                        <Link
                            display={{base: 'flex', lg: 'none'}}
                            href="/account"
                            alignItems="center"
                        >
                            <LeftArrow />
                        </Link>
                        <Text>
                            <FormattedMessage
                                id="account_detail.communication_preferences"
                                defaultMessage="Communication Preferences"
                            />
                        </Text>
                    </Flex>
                </Heading>

                {form.formState.errors?.global && (
                    <Alert status="error" maxW={'max-content'}>
                        <AlertIcon color="red.500" boxSize={4} />
                        <Text fontSize="sm" ml={3}>
                            {form.formState.errors.global.message}
                        </Text>
                    </Alert>
                )}

                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Stack spacing={{base: '12', lg: '16'}}>
                        {/* Transactional email notice */}
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            gap={'12px'}
                            data-testid="communication-transactional-email-notice"
                        >
                            <Flex gap={'8px'} data-testid="communication-transactional-email-title">
                                <InfoCircleBlack boxSize={'20px'} />
                                <Text fontSize={{base: 'xs', md: 'sm'}} fontWeight={'500'}>
                                    <FormattedMessage
                                        id="account_detail.transactional_emails_cannot_unsubscribe"
                                        defaultMessage="Transactional emails can’t be unsubscribed"
                                    />
                                </Text>
                            </Flex>
                            <Text
                                fontSize={'xs'}
                                fontWeight={'400'}
                                data-testid="communication-transactional-email-description"
                            >
                                <FormattedMessage
                                    id="account_detail.transactional_emails_unsubscribe_note"
                                    defaultMessage="You cannot unsubscribe from transactional emails (including order confirmation or cancellation emails)."
                                />
                            </Text>
                        </Box>

                        {/* Language preference section */}
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            gap={'16px'}
                            data-testid="communication-language-section"
                        >
                            <Heading
                                as="h3"
                                fontSize={{base: 'xs', lg: 'sm'}}
                                fontWeight={'700'}
                                textTransform={'uppercase'}
                                data-testid="communication-language-title"
                            >
                                <FormattedMessage
                                    id="account_detail.language_label"
                                    defaultMessage="Language"
                                />
                            </Heading>
                            <Box
                                display={'flex'}
                                flexDirection={'column'}
                                gap={'8px'}
                                data-testid="communication-language-options"
                            >
                                <Field
                                    {...fields.languagePreference}
                                    labelProps={{
                                        fontSize: '14px',
                                        fontWeight: '400',
                                        ml: 0
                                    }}
                                    radioGroupProps={{
                                        spacing: 2
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Email communication preferences section */}
                        {isSfscEmailSubscriptionEnabled && (
                            <Box
                                display={'flex'}
                                flexDirection={'column'}
                                gap={'16px'}
                                data-testid="communication-email-section"
                            >
                                <Heading
                                    as="h3"
                                    fontSize={{base: 'xs', lg: 'sm'}}
                                    fontWeight={'700'}
                                    textTransform={'uppercase'}
                                    data-testid="communication-email-title"
                                >
                                    <FormattedMessage
                                        id="account_detail.email_notifications_label"
                                        defaultMessage="Email Notifications"
                                    />
                                </Heading>
                                <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                                    <Text
                                        fontSize={'sm'}
                                        fontWeight={'400'}
                                        data-testid="communication-email-general-description"
                                    >
                                        <FormattedMessage
                                            id="communication_preferences.email.general.description"
                                            defaultMessage="Receive special offers, store news and updates on the latest fashion trends"
                                        />
                                    </Text>
                                    <Field
                                        {...fields.emailGarageOptIn}
                                        data-testid="communication-email-general-switch"
                                    />
                                </Box>
                                <Divider
                                    width={{base: 'full', lg: '458px'}}
                                    height="1px"
                                    bg="#ECE9E9"
                                    data-testid="communication-email-divider"
                                />
                                <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                                    <Text fontSize={'sm'} fontWeight={'400'}>
                                        <FormattedMessage
                                            id="communication_preferences.email.garage.description"
                                            defaultMessage="Receive GARAGE COLLECTIF notifications and special personalized recommendations specifically tailored for you"
                                        />
                                    </Text>
                                    <Field
                                        {...fields.emailGaragePersonalizedOptIn}
                                        data-testid="communication-email-garage-switch"
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* SMS communication preferences section */}
                        {isSMSOptInEnabled && (
                            <Box
                                data-testid="communication-sms-section"
                                display={'flex'}
                                flexDirection={'column'}
                                gap={'16px'}
                            >
                                <Heading
                                    as="h3"
                                    fontSize={{base: 'xs', lg: 'sm'}}
                                    fontWeight={'700'}
                                    textTransform={'uppercase'}
                                >
                                    <FormattedMessage
                                        id="communication_preferences.sms.title"
                                        defaultMessage="SMS Notifications"
                                    />
                                </Heading>

                                {isSmsSubscribed ? (
                                    <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                                        <Flex gap={'5px'}>
                                            <CheckCircleIcon boxSize={'20px'} />
                                            <Text fontSize={'sm'} fontWeight={'400'}>
                                                <FormattedMessage
                                                    id="account_detail.subscribed_to_label"
                                                    defaultMessage="Subscribed to"
                                                />
                                                {tempPhone ? `${maskPhone(tempPhone)}` : ''}
                                            </Text>
                                        </Flex>
                                        <Text fontSize={'xs'} ml={'3px'} fontWeight={'400'} color={'gray.700'}>
                                            <FormattedMessage
                                                id="account_detail.sms_unsubscribe_disclaimer"
                                                defaultMessage="You can unsubscribe at any time by replying STOP to a text message. Message frequency varies. Standard message & data rates may apply."
                                            />
                                        </Text>
                                    </Box>
                                ) : (
                                    <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                                        <Flex flexDirection={'column'} gap={'20px'}>
                                            <Text fontSize="sm" fontWeight={'400'}>
                                                <FormattedMessage
                                                    id="communication_preferences.sms.description"
                                                    defaultMessage="Get special offers, early access to drops, and exclusive updates by text."
                                                />
                                            </Text>
                                            <Text fontSize="sm" fontWeight={'700'}>
                                                <FormattedMessage
                                                    id="communication_preferences.earn_bonus_points_on_subscribe"
                                                    defaultMessage="Earn 10 bonus points when you subscribe."
                                                />
                                            </Text>
                                        </Flex>
                                        <Field {...fields.smsPhoneNumber} />
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                const phone = form.getValues(
                                                    `${prefix}smsPhoneNumber`
                                                )

                                                if (!phone || phone.length < 7) return

                                                setTempPhone(phone)
                                                setIsSmsSubscribed(true)
                                            }}
                                            fontSize="sm"
                                            fontWeight="700"
                                            border={'1px solid black'}
                                            width={{base: 'full', md: '224px'}}
                                            height={'48px'}
                                            textTransform="uppercase"
                                            isDisabled={false}
                                            data-testid="communication-sms-subscribe-button"
                                        >
                                            <FormattedMessage
                                                id="communication_preferences.sms.subscribe"
                                                defaultMessage="Subscribe"
                                            />
                                        </Button>

                                        <Text fontSize="xs" fontWeight={'400'} color={'gray.700'}>
                                            <FormattedMessage
                                                id="communication_preferences.sms.disclaimer"
                                                defaultMessage="By subscribing, you agree to receive marketing text messages from Garage. Msg & data rates may apply. Reply STOP to unsubscribe. View {termsLink} and {privacyLink}."
                                                values={{
                                                    termsLink: (
                                                        <Link
                                                            href="/terms-conditions.html"
                                                            textDecoration="underline"
                                                        >
                                                            <FormattedMessage
                                                                defaultMessage="Terms & Conditions"
                                                                id="login_form.link.terms_conditions"
                                                            />
                                                        </Link>
                                                    ),
                                                    privacyLink: (
                                                        <Link
                                                            href="/privacy-policy.html"
                                                            textDecoration="underline"
                                                        >
                                                            <FormattedMessage
                                                                defaultMessage="Privacy Policy"
                                                                id="login_form.link.privacy_policy"
                                                            />
                                                        </Link>
                                                    )
                                                }}
                                            />
                                        </Text>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Stack>
                    <Box>
                        <Button
                            data-testid="communication-button-save-changes"
                            variant="solid"
                            type="submit"
                            fontSize="sm"
                            fontWeight="700"
                            textTransform="uppercase"
                            width={{base: 'full', md: '224px'}}
                            height={'48px'}
                            mt={'48px'}
                            onClick={() => {
                                form.clearErrors('global')
                            }}
                            isLoading={isLoading}
                            loadingText={formatMessage({
                                id: 'communication_preferences.sms.saving',
                                defaultMessage: 'Saving...'
                            })}
                        >
                            <FormattedMessage
                                id="communication_preferences.save_changes"
                                defaultMessage="Save Changes"
                            />
                        </Button>
                    </Box>
                </form>
            </Stack>
            {/* Removing for MVP */}
            {/* {isSMSOptInEnabled && <SMSOptInModal isOpen={isOpen} onClose={onClose} />} */}
        </Box>
    )
}

CommunicationPreferences.propTypes = {
    prefix: PropTypes.string
}

export default CommunicationPreferences
